import React, { useState, useEffect } from 'react';
import logo from '../images/logo.png'; // Adjust the path based on your project structure

const Footer = () => {
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [hoveredLink, setHoveredLink] = useState(null);

  const footerSections = [
    {
      title: 'Services',
      links: [
        { name: 'Medicine Delivery', href: '#', icon: '💊' },
        { name: 'Prescription', href: '#', icon: '📋' },
        { name: 'Pharmacy Support', href: '#', icon: '👨‍⚕️' },
        { name: 'Health Products', href: '#', icon: '🏥' },
        { name: 'Order Tracking', href: '#', icon: '🚚' }
      ]
    },
    {
      title: 'Company',
      links: [
        { name: 'About Us', href: '#', icon: '🏢' },
        { name: 'Our Mission', href: '#', icon: '🎯' },
        { name: 'Leadership', href: '#', icon: '👨‍💼' },
        { name: 'Careers', href: '#', icon: '💼' },
        { name: 'Blog', href: '#', icon: '✍️' }
      ]
    },
    {
      title: 'Support',
      links: [
        { name: 'Help Center', href: '#', icon: '❓' },
        { name: 'Contact Us', href: '#', icon: '📞' },
        { name: 'Order Status', href: '#', icon: '📦' },
        { name: 'Delivery Areas', href: '#', icon: '🗺️' },
        { name: 'FAQ', href: '#', icon: '💡' }
      ]
    },
    {
      title: 'Legal',
      links: [
        { name: 'Privacy Policy', href: '#', icon: '🔒' },
        { name: 'Terms of Service', href: '#', icon: '📄' },
        { name: 'Cookie Policy', href: '#', icon: '🍪' },
        { name: 'Data Security', href: '#', icon: '🛡️' },
        { name: 'Compliance', href: '#', icon: '✅' }
      ]
    }
  ];

  const socialLinks = [
    { name: 'WhatsApp', href: '#', icon: '💬', color: 'hover:text-green-400' },
    { name: 'Instagram', href: '#', icon: '📷', color: 'hover:text-pink-400' },
    { name: 'Twitter', href: '#', icon: '🐦', color: 'hover:text-cyan-400' },
    { name: 'Facebook', href: '#', icon: '👥', color: 'hover:text-blue-500' },
    { name: 'LinkedIn', href: '#', icon: '💼', color: 'hover:text-blue-400' }
  ];

  // Pharmacy floating icons
  const pharmacyFloatingIcons = [
    { icon: '💊', size: 'w-8 h-8', position: 'top-10 left-10', delay: 'delay-0' },
    { icon: '🏪', size: 'w-6 h-6', position: 'top-20 right-20', delay: 'delay-1000' },
    { icon: '🚚', size: 'w-7 h-7', position: 'bottom-20 left-20', delay: 'delay-2000' },
    { icon: '📦', size: 'w-5 h-5', position: 'bottom-10 right-10', delay: 'delay-1500' }
  ];

  return (
    <footer className="bg-gradient-to-br from-indigo-900 via-blue-800 to-purple-900 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-20 -left-20 w-40 h-40 bg-cyan-500/10 rounded-full blur-2xl animate-pulse"></div>
        <div className="absolute -bottom-20 -right-20 w-40 h-40 bg-purple-500/10 rounded-full blur-2xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 w-32 h-32 bg-blue-500/5 rounded-full blur-xl animate-pulse delay-500"></div>
      </div>

      {/* Floating Pharmacy Icons */}
      {pharmacyFloatingIcons.map((item, index) => (
        <div
          key={index}
          className={`absolute ${item.position} ${item.size} bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 flex items-center justify-center text-sm animate-float-slow ${item.delay} z-20`}
        >
          {item.icon}
        </div>
      ))}

      {/* Main Footer Content */}
      <div className="relative z-10">
        {/* Top Section */}
        <div className="border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8">
              {/* Brand Section */}
              <div className="lg:col-span-2">
                <div className="flex items-center space-x-3 mb-6">
                  {/* Continuously Animated Logo Container */}
                  <div className="relative">
                    {/* Gradient Background */}
                    <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-purple-500 rounded-2xl blur-md opacity-70 scale-110 animate-pulse-slow"></div>

                    {/* Logo Image with Continuous Animations */}
                    <div className="relative w-12 h-12 bg-white rounded-2xl flex items-center justify-center animate-float">
                      <img
                        src={logo}
                        alt="Clynix Pharmacy Logo"
                        className="w-10 h-10 object-contain animate-gentle-scale"
                      />

                      {/* Continuous Pulse Ring Effect */}
                      <div className="absolute inset-0 border-2 border-cyan-400 rounded-2xl animate-ping-slow opacity-30"></div>
                    </div>

                    {/* Continuous Floating Particles */}
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-cyan-400 rounded-full animate-ping"></div>
                    <div className="absolute -bottom-1 -left-1 w-2 h-2 bg-purple-400 rounded-full animate-ping delay-700"></div>
                    <div className="absolute -top-1 -left-1 w-1 h-1 bg-cyan-300 rounded-full animate-ping delay-1400"></div>
                  </div>

                  <div>
                    <h3 className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
                      CLYNIX
                    </h3>
                    <p className="text-gray-400 text-sm">Your Trusted Pharmacy</p>
                  </div>
                </div>
                <p className="text-gray-300 mb-6 leading-relaxed">
                  Delivering authentic medicines to your doorstep with 2-hour delivery promise.
                  Your health and safety are our top priorities.
                </p>

                {/* Contact Info */}
                <div className="space-y-2 mb-6">
                  <div className="flex items-center space-x-2 text-gray-300">
                    <span>📧</span>
                    <span className="text-sm">simcurarx@gmail.com</span>
                  </div>
                  <div className="flex items-center space-x-2 text-gray-300">
                    <span>🏢</span>
                  </div>
                  <div className="flex items-center space-x-2 text-gray-300">
                    <span>📞</span>
                    <span className="text-sm">+91 8309056333</span>
                  </div>
                </div>


                {/* Social Links */}
                <div className="flex space-x-4">
                  {socialLinks.map((social, index) => (
                    <a
                      key={index}
                      href={social.href}
                      className={`w-10 h-10 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 flex items-center justify-center text-gray-400 ${social.color} transition-all duration-300 hover:scale-110 hover:bg-white/10 transform hover:-translate-y-1`}
                      onMouseEnter={() => setHoveredLink(social.name)}
                      onMouseLeave={() => setHoveredLink(null)}
                    >
                      <span className="text-lg">{social.icon}</span>
                    </a>
                  ))}
                </div>
              </div>

              {/* Footer Links */}
              {footerSections.map((section, index) => (
                <div key={index} className="space-y-4">
                  <h4 className="text-white font-bold text-lg mb-4 flex items-center">
                    <span className="w-2 h-2 bg-cyan-400 rounded-full mr-2 animate-pulse"></span>
                    {section.title}
                  </h4>
                  <ul className="space-y-3">
                    {section.links.map((link, linkIndex) => (
                      <li key={linkIndex}>
                        <a
                          href={link.href}
                          className="flex items-center space-x-2 text-gray-400 hover:text-cyan-400 transition-all duration-300 group"
                          onMouseEnter={() => setHoveredLink(link.name)}
                          onMouseLeave={() => setHoveredLink(null)}
                        >
                          <span className="text-sm transform group-hover:scale-110 transition-transform duration-300">
                            {link.icon}
                          </span>
                          <span className="group-hover:translate-x-1 transition-transform duration-300">
                            {link.name}
                          </span>
                          {hoveredLink === link.name && (
                            <span className="w-1 h-1 bg-cyan-400 rounded-full animate-ping"></span>
                          )}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Middle Section - Features */}
        <div className="border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { icon: '🚚', text: '2-Hour Delivery', sub: 'Fast & Reliable' },
                { icon: '💊', text: 'Authentic Medicines', sub: '100% Genuine' },
                { icon: '🔒', text: 'Secure Payments', sub: 'Safe & Encrypted' },
                { icon: '👨‍⚕️', text: 'Pharmacy Support', sub: '24/7 Available' }
              ].map((feature, index) => (
                <div
                  key={index}
                  className="text-center p-4 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 hover:bg-white/10 transition-all duration-300 hover:scale-105 group"
                >
                  <div className="text-2xl mb-2 transform group-hover:scale-110 transition-transform duration-300">
                    {feature.icon}
                  </div>
                  <div className="text-white font-semibold text-sm">{feature.text}</div>
                  <div className="text-cyan-400 text-xs">{feature.sub}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            {/* Copyright */}
            <div className="text-gray-400 text-sm">
              © {currentYear} Clynix Pharmacy. All rights reserved.
              <span className="text-cyan-400 ml-2">Delivering Health to Your Doorstep</span>
            </div>

            {/* Legal Links */}
            <div className="flex flex-wrap justify-center space-x-6">
              {[
                { name: 'Privacy Policy', href: '#' },
                { name: 'Terms of Service', href: '#' },
                { name: 'Cookie Policy', href: '#' },
                { name: 'Data Security', href: '#' },
                { name: 'Compliance', href: '#' }
              ].map((link, index) => (
                <a
                  key={index}
                  href={link.href}
                  className="text-gray-400 hover:text-cyan-400 text-sm transition-all duration-300 hover:scale-105"
                >
                  {link.name}
                </a>
              ))}
            </div>

            {/* Trust Badges */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 bg-green-500/20 px-3 py-1 rounded-full border border-green-400/30">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                <span className="text-green-400 text-xs font-semibold">Verified Pharmacy</span>
              </div>
              <div className="flex items-center space-x-2 bg-blue-500/20 px-3 py-1 rounded-full border border-blue-400/30">
                <span className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></span>
                <span className="text-blue-400 text-xs font-semibold">Secure Delivery</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Animations */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { 
            transform: translateY(0px) rotate(0deg) scale(1); 
          }
          33% { 
            transform: translateY(-3px) rotate(2deg) scale(1.02); 
          }
          66% { 
            transform: translateY(2px) rotate(-1deg) scale(0.98); 
          }
        }
        
        @keyframes gentle-scale {
          0%, 100% { 
            transform: scale(1); 
          }
          50% { 
            transform: scale(1.05); 
          }
        }
        
        @keyframes pulse-slow {
          0%, 100% { 
            opacity: 0.5;
            transform: scale(1);
          }
          50% { 
            opacity: 0.7;
            transform: scale(1.1);
          }
        }
        
        @keyframes ping-slow {
          0%, 100% { 
            transform: scale(1);
            opacity: 0.3;
          }
          50% { 
            transform: scale(1.5);
            opacity: 0;
          }
        }

        @keyframes float-slow {
          0%, 100% { 
            transform: translateY(0px) rotate(0deg); 
          }
          33% { 
            transform: translateY(-8px) rotate(3deg); 
          }
          66% { 
            transform: translateY(4px) rotate(-2deg); 
          }
        }
        
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        
        .animate-gentle-scale {
          animation: gentle-scale 4s ease-in-out infinite;
        }
        
        .animate-pulse-slow {
          animation: pulse-slow 3s ease-in-out infinite;
        }
        
        .animate-ping-slow {
          animation: ping-slow 2s cubic-bezier(0, 0, 0.2, 1) infinite;
        }

        .animate-float-slow {
          animation: float-slow 8s ease-in-out infinite;
        }
      `}</style>
    </footer>
  );
};

export default Footer;